import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { Clock, FileText, Search, SlidersHorizontal, X } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/state/redux/hooks";
import { selectViewingRecord, setViewingRecord } from "@/state/redux/slices/ui";
import {
  deserializeRecord,
  intervalNames,
  serializeRecord,
} from "@/common/record";
import { HGCollectionInfo } from "@/common/types";
import { useBreakpoint, useElementWidth } from "@/common/hooks";
import { useCollectionFetching } from "@/api/hooks";
import {
  RecordsViewOptions,
  defaultRecordsViewOptions,
} from "@/state/recordsView";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { iconButtonClass } from "./RecordsToolbar";

interface RecordsBandProps {
  collection: HGCollectionInfo | undefined;
  /** Records currently listed. `null` while the first load is still out. */
  count: number | null;
  /** Free-text filter over the listed records; owned by `Records`. */
  query: string;
  setQuery: (value: string) => void;
  /**
   * Whether notes are actually being shown. Owned by `Records` so band, toolbar
   * and body can never disagree: `options.mode` alone is not enough, since a
   * stored "notes" mode falls back to the timeline outside a collection.
   */
  notesMode: boolean;
  /** Notes live in a collection, so the mode switch only exists inside one. */
  showModeSwitch: boolean;
  options: RecordsViewOptions;
  setOption: <K extends keyof RecordsViewOptions>(
    key: K,
    value: RecordsViewOptions[K],
  ) => void;
}

/** Below this the band drops its labels; measured on the band, never the window. */
const COMPACT_W = 420;

/**
 * Whether anything the folded toolbar holds is off its default.
 *
 * The toolbar is shut by default and `collectionScope`, `showGaps` and the
 * grouping all survive a reload, so without this a list narrowed months ago
 * stays narrowed with nothing on screen to say why or where to change it.
 */
function optionsAreCustom(options: RecordsViewOptions, notesMode: boolean) {
  const d = defaultRecordsViewOptions;
  if (notesMode) {
    return (
      options.notesSort !== d.notesSort || options.notesOrder !== d.notesOrder
    );
  }
  return (
    options.collectionScope !== d.collectionScope ||
    options.groupBy !== d.groupBy ||
    options.thenBy !== d.thenBy ||
    options.order !== d.order ||
    options.spanning !== d.spanning ||
    options.showGaps !== d.showGaps
  );
}

/**
 * The list's own header: what is being listed, how much of it, and the controls
 * that change which list it is.
 *
 * It sits above the toolbar and outside the scroll container, and it is always
 * on screen. That last part is what the record heading a collection needs: a
 * title record does not belong to the collection it titles, so it is never in
 * the list below, and its own date may sit outside the range being viewed —
 * a row in the flow would come and go as the user navigates time. So the band
 * carries the collection instead, clickable when there is a record behind the
 * name and plain text when there is not.
 *
 * Must stay an unconditional child of `Records`' column at a stable position in
 * the JSX: `autoOpened` below is a ref, and remounting the band would let a
 * collection's title record re-open itself every time the user came back.
 */
export default function RecordsBand({
  collection,
  count,
  query,
  setQuery,
  notesMode,
  showModeSwitch,
  options,
  setOption,
}: RecordsBandProps) {
  const dispatch = useAppDispatch();
  const viewingRecord = useAppSelector(selectViewingRecord);
  const { is2Xl } = useBreakpoint();
  const { userID, collectionID: collectionRef } = useParams();
  const collectionFetching = useCollectionFetching();
  const [rootRef, width] = useElementWidth<HTMLDivElement>();
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const compact = width < COMPACT_W;

  const serialized = collection?.title_record ?? null;
  // The embedded record comes straight from the API, so unlike the ones in the
  // timeline it has never been through enrichRecords and carries no interval
  // names. They are attached here rather than at the point of display, because
  // clicking the band hands this same record to the preview pane, which reads
  // them too.
  const record = useMemo(() => {
    const deserialized = deserializeRecord(serialized);
    if (!deserialized) return null;
    return { ...deserialized, ...intervalNames(deserialized) };
  }, [serialized]);

  // Opening a collection that has a title record opens that record, once. Only
  // where there is a preview pane to open it into — below 2xl the preview takes
  // the whole screen, and landing on a collection would mean landing on a record
  // instead of its timeline. Keyed on the collection so it happens once per
  // collection: closing the preview, or opening another record, stands until the
  // user goes somewhere else.
  const autoOpened = useRef<string | null>(null);
  useEffect(() => {
    if (!is2Xl || !record || !serialized) return;
    if (autoOpened.current === collection?.id) return;
    autoOpened.current = collection?.id ?? null;
    dispatch(setViewingRecord(serializeRecord(record)));
  }, [is2Xl, record, serialized, collection?.id, dispatch]);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const closeSearch = () => {
    setSearchOpen(false);
    setQuery("");
  };

  const selected = !!record && viewingRecord?.id === record.id;
  // The collection's own name, always — the title record's may differ, and it is
  // the collection this band speaks for. The two states the header used to
  // report come with it, since it no longer names the collection at all.
  const name =
    collection?.title ??
    (collectionFetching ? "Loading…" : "Collection is not accessible");
  const nameClass =
    "min-w-0 truncate text-left font-serif text-[14.5px] font-semibold leading-[1.28]";

  const customOptions = optionsAreCustom(options, notesMode);
  // The URL's own spelling of the space, which is where the user came from. The
  // resolved owner is not available in one's own space, and `useCanonicalPath`
  // rewrites a stale ref on arrival anyway.
  const spacePath = userID ? `/${userID}` : "/";

  return (
    <div
      ref={rootRef}
      className="flex h-[44px] min-w-0 shrink-0 items-center gap-1 overflow-hidden border-b border-border bg-card pl-[22.5px] pr-2"
    >
      {/* Only the left half ever changes shape. The search box's blur handler
          fires on mousedown, before a click lands, so a right-hand control that
          moved as the box closed would be missed by the click that closed it. */}
      {searchOpen ? (
        <div className="relative flex min-w-0 flex-1 items-center">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") closeSearch();
            }}
            onBlur={() => {
              if (!query) setSearchOpen(false);
            }}
            placeholder={notesMode ? "Search notes..." : "Search records..."}
            className="h-7 w-full min-w-0 pl-7 text-[13px]"
          />
        </div>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {collectionRef && (
            <>
              {/* The accent rule is the whole visual argument: this band is the
                  collection speaking for itself, not the first row of its list.
                  Its centre lands on the timeline's spine. */}
              <span
                aria-hidden
                className="h-[22px] w-[3px] shrink-0 rounded-full bg-primary"
              />
              {record ? (
                <button
                  type="button"
                  onClick={() =>
                    dispatch(
                      setViewingRecord(
                        selected ? null : serializeRecord(record),
                      ),
                    )
                  }
                  aria-pressed={selected}
                  title={`Open ${name}`}
                  className={`${nameClass} rounded-sm px-1 py-0.5 transition-colors hover:bg-row-hover ${
                    selected
                      ? "bg-accent text-accent-foreground"
                      : "text-foreground"
                  }`}
                  style={{ fontFamily: "var(--font-serif)" }}
                >
                  {name}
                </button>
              ) : (
                <span
                  className={`${nameClass} px-1 py-0.5 text-foreground`}
                  style={{ fontFamily: "var(--font-serif)" }}
                >
                  {name}
                </span>
              )}
            </>
          )}
          {count !== null && (
            <span className="shrink-0 whitespace-nowrap font-mono text-[12px] tabular-nums text-muted-foreground">
              {collectionRef && <span className="pr-1 text-ink2">·</span>}
              {count.toLocaleString()}
              {!compact &&
                ` ${notesMode ? (count === 1 ? "note" : "notes") : count === 1 ? "record" : "records"}`}
            </span>
          )}
        </div>
      )}

      {/* Constant width, and the same set in both search states. */}
      <div className="flex shrink-0 items-center gap-0.5">
        {showModeSwitch && (
          <div className="mr-1 flex shrink-0 gap-[3px] rounded-md border border-border bg-background p-[3px]">
            {(["time", "notes"] as const).map((mode) => {
              const active = mode === "notes" ? notesMode : !notesMode;
              const Icon = mode === "time" ? Clock : FileText;
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setOption("mode", mode)}
                  title={mode === "time" ? "Timeline" : "Notes"}
                  className={`flex h-[24px] items-center gap-1 rounded-[5px] px-2 text-[12.5px] transition-colors ${
                    active
                      ? "bg-primary font-semibold text-primary-foreground"
                      : "text-muted-foreground hover:bg-row-hover hover:text-foreground"
                  }`}
                >
                  <Icon className="size-3.5" />
                  {!compact && (mode === "time" ? "Time" : "Notes")}
                </button>
              );
            })}
          </div>
        )}

        <Button
          variant="ghost"
          size="icon"
          className={iconButtonClass}
          title={searchOpen ? "Close search" : "Search"}
          onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}
        >
          {searchOpen ? (
            <X className="size-[15px]" />
          ) : (
            <Search className="size-[15px]" />
          )}
        </Button>

        <Button
          variant="ghost"
          size="icon"
          aria-expanded={options.toolbarOpen}
          aria-controls="records-toolbar"
          className={`${iconButtonClass} relative ${
            options.toolbarOpen ? "bg-row-hover text-foreground" : ""
          }`}
          title={options.toolbarOpen ? "Hide view options" : "View options"}
          onClick={() => setOption("toolbarOpen", !options.toolbarOpen)}
        >
          <SlidersHorizontal className="size-[15px]" />
          {/* Something in there is not on its default and the panel is shut. */}
          {customOptions && !options.toolbarOpen && (
            <span
              aria-hidden
              className="absolute right-[5px] top-[5px] size-[5px] rounded-full bg-primary"
            />
          )}
        </Button>

        {collectionRef && (
          <>
            <span aria-hidden className="mx-1 h-4 w-px shrink-0 bg-border" />
            {/* A real link, not a navigate() handler: middle-click and
                open-in-new-tab are worth keeping on a destination. */}
            <Button
              variant="ghost"
              size="icon"
              className={iconButtonClass}
              asChild
            >
              <Link
                to={spacePath}
                title="Close this collection"
                aria-label="Close this collection"
              >
                <X className="size-[15px]" />
              </Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
